import React from 'react';
import { SparklesIcon, UsersIcon } from './icons';
import { ImageFile, Character, SequenceVideoData, PromptSequence, StoryboardPreview } from '../types';
import { base64ToFile } from '../utils/base64';
import { DraggableKeyframe } from './DraggableKeyframe';

interface PromptConceptionProps {
    motionDescription: string | null;
    referenceImage: ImageFile | null;
    activeChatImage: ImageFile | null;
    mentionedCharacters: Character[];
    sequenceHistory?: SequenceVideoData[];
    // === NANO BANANA PRO: Thumbnail Retouche ===
    onOpenNanoEditor?: (segmentIndex: number, baseImage: ImageFile, initialPrompt: string) => void;
    promptSequence?: PromptSequence | null;
    storyboardByIndex?: Record<number, StoryboardPreview>;
    // === IMAGE-FIRST: Keyframe generation status ===
    isGeneratingKeyframes?: boolean;
    // === P0.4: Use as Start/End Image callbacks ===
    onUseAsStartImage?: (segmentIndex: number, image: ImageFile) => void;
    onUseAsEndImage?: (segmentIndex: number, image: ImageFile) => void;
    /** Index of keyframe currently used as start image */
    activeStartImageIndex?: number | null;

    apiKey?: string | null; // P0.6: BYOK Strict
}

export const PromptConception: React.FC<PromptConceptionProps> = ({
    motionDescription,
    referenceImage,
    activeChatImage,
    mentionedCharacters,
    sequenceHistory = [],
    onOpenNanoEditor,
    promptSequence,
    storyboardByIndex = {},
    isGeneratingKeyframes = false,
    onUseAsStartImage,
    onUseAsEndImage,
    activeStartImageIndex,

    apiKey,
}) => {
    const displayImage = activeChatImage || referenceImage;
    const hasContent =
        motionDescription || displayImage || mentionedCharacters.length > 0 || sequenceHistory.length > 0;

    // Determine the main title based on context
    const title = motionDescription
        ? 'Vecteur de Continuité'
        : displayImage
            ? 'Référence Active'
            : mentionedCharacters.length > 0
                ? 'Personnages Actifs'
                : 'Conception du Prompt';

    // Helper: Get preview badge status
    const getPreviewStatus = (segmentIndex: number): 'ok' | 'missing' | 'dirty' => {
        // Check if dirty (extension in dirtyExtensions)
        if (segmentIndex >= 1 && promptSequence?.dirtyExtensions?.includes(segmentIndex)) {
            return 'dirty';
        }
        // Check if storyboard preview exists
        if (storyboardByIndex[segmentIndex]) {
            return 'ok';
        }
        return 'missing';
    };

    // Helper: Get prompt for segment
    const getPromptForSegment = (segmentIndex: number): string => {
        if (!promptSequence) return '';
        if (segmentIndex === 0) return promptSequence.mainPrompt;
        return promptSequence.extensionPrompts[segmentIndex - 1] || '';
    };

    // Helper: Handle thumbnail click for Nano
    const handleThumbnailNano = (segmentIndex: number, thumbnail: string | undefined) => {
        if (!onOpenNanoEditor || !thumbnail) return;

        const baseImage: ImageFile = {
            file: base64ToFile(thumbnail, `thumbnail_${segmentIndex}.jpg`, 'image/jpeg'),
            base64: thumbnail,
        };
        const initialPrompt = getPromptForSegment(segmentIndex);
        onOpenNanoEditor(segmentIndex, baseImage, initialPrompt);
    };

    return (
        <div className="bg-[#1f1f1f] border border-gray-700 rounded-2xl h-full flex flex-col shadow-lg p-4 transition-all duration-500">
            <h3 className="text-lg font-semibold text-white mb-4 flex-shrink-0 flex items-center gap-2">
                <SparklesIcon className="w-5 h-5 text-indigo-400" />
                {title}
            </h3>
            <div className="flex-grow flex flex-col gap-4 overflow-y-auto">
                {!hasContent ? (
                    <div className="flex-grow flex flex-col items-center justify-center text-center p-4 border-2 border-dashed border-gray-800 rounded-xl bg-gray-900/50">
                        <div className="w-12 h-12 rounded-full bg-gray-800 flex items-center justify-center mb-3 animate-pulse">
                            <UsersIcon className="w-6 h-6 text-gray-600" />
                        </div>
                        <p className="text-gray-400 font-medium">Context Radar Active</p>
                        <p className="text-xs text-gray-600 mt-1 max-w-[200px]">
                            Mention characters with "@", upload images in chat, or use Studio references to populate this panel.
                        </p>
                    </div>
                ) : (
                    <>
                        {/* Feature 2: Time Injection - Timeline Visualization with Nano Actions */}
                        {sequenceHistory.length > 0 && (
                            <div className="flex flex-col gap-2 bg-gray-800/50 p-2 rounded-xl border border-gray-700/50">
                                <div className="text-xs font-bold text-indigo-400 uppercase tracking-wider flex justify-between items-center">
                                    <span>Sequence Flow</span>
                                    <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-indigo-900 text-indigo-300">{sequenceHistory.length} shots</span>
                                </div>
                                <div className="flex gap-2 overflow-x-auto pb-2 snap-x">
                                    {sequenceHistory.map((data, idx) => {
                                        const previewStatus = getPreviewStatus(idx);

                                        return (
                                            <div key={idx} className="flex-shrink-0 w-24 snap-start relative group">
                                                <div className="aspect-video rounded-md overflow-hidden border border-gray-600 relative">
                                                    {data.thumbnail ? (
                                                        <img src={`data:image/jpeg;base64,${data.thumbnail}`} className="w-full h-full object-cover" alt={`Shot ${idx + 1}`} />
                                                    ) : (
                                                        <div className="w-full h-full bg-gray-900 flex items-center justify-center text-xs text-gray-500">Shot {idx + 1}</div>
                                                    )}

                                                    {/* Nano Hover Button */}
                                                    {onOpenNanoEditor && data.thumbnail && (
                                                        <button
                                                            onClick={() => handleThumbnailNano(idx, data.thumbnail)}
                                                            className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
                                                        >
                                                            <span className="px-2 py-1 bg-orange-600 text-white text-[9px] font-semibold rounded-md shadow-lg flex items-center gap-1">
                                                                <SparklesIcon className="w-3 h-3" />
                                                                Nano
                                                            </span>
                                                        </button>
                                                    )}

                                                    {/* Preview Badge */}
                                                    <div className={`absolute top-1 right-1 px-1 py-0.5 text-[8px] font-bold rounded ${previewStatus === 'ok'
                                                        ? 'bg-green-600/80 text-green-100'
                                                        : previewStatus === 'dirty'
                                                            ? 'bg-orange-600/80 text-orange-100'
                                                            : 'bg-gray-600/80 text-gray-300'
                                                        }`}>
                                                        {previewStatus === 'ok' ? 'OK' : previewStatus === 'dirty' ? '⚠' : '—'}
                                                    </div>
                                                </div>
                                                <div className="text-[10px] text-center text-gray-500 mt-1">
                                                    {idx === 0 ? 'Root' : `Ext ${idx}`}
                                                </div>
                                                {idx === sequenceHistory.length - 1 && (
                                                    <div className="absolute -right-1 top-1/2 -translate-y-1/2 w-2 h-2 bg-green-500 rounded-full animate-pulse shadow-[0_0_8px_rgba(34,197,94,0.8)]" title="Current Anchor" />
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}

                        {/* === IMAGE-FIRST: Auto-Keyframes Panel (P0.4: Draggable) === */}
                        {(storyboardByIndex[0] || storyboardByIndex[1]) && (
                            <div className="flex flex-col gap-2 bg-indigo-900/20 p-3 rounded-xl border border-indigo-600/30">
                                <div className="text-xs font-bold text-indigo-400 uppercase tracking-wider flex justify-between items-center">
                                    <span>🎬 Keyframes</span>
                                    <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-indigo-800 text-indigo-200">Drag to Use</span>
                                </div>
                                <div className="flex gap-3">
                                    {/* Root Keyframe (Segment 0) */}
                                    {storyboardByIndex[0] && (
                                        <div className="flex-1">
                                            <DraggableKeyframe
                                                segmentIndex={0}
                                                preview={storyboardByIndex[0]}
                                                onEdit={onOpenNanoEditor}
                                                onUseAsStartImage={onUseAsStartImage}
                                                isActiveStartImage={activeStartImageIndex === 0}
                                                isGenerating={isGeneratingKeyframes}
                                                label="ROOT"
                                                borderColor="border-indigo-500/50"
                                                prompt={getPromptForSegment(0)}
                                                size="medium"
                                            />
                                        </div>
                                    )}
                                    {/* Extension 1 Keyframe (Segment 1) */}
                                    {storyboardByIndex[1] && (
                                        <div className="flex-1">
                                            <DraggableKeyframe
                                                segmentIndex={1}
                                                preview={storyboardByIndex[1]}
                                                onEdit={onOpenNanoEditor}
                                                onUseAsStartImage={onUseAsStartImage}
                                                onUseAsEndImage={onUseAsEndImage}
                                                isActiveStartImage={activeStartImageIndex === 1}
                                                isGenerating={isGeneratingKeyframes}
                                                label={`EXT 1 ${getPreviewStatus(1) === 'dirty' ? '⚠' : ''}`}
                                                borderColor={getPreviewStatus(1) === 'dirty' ? 'border-orange-500/50' : 'border-purple-500/50'}
                                                prompt={getPromptForSegment(1)}
                                                size="medium"
                                            />
                                        </div>
                                    )}
                                </div>
                                <p className="text-[9px] text-gray-500 text-center">
                                    Cliquez "Start Image" ou glissez vers le slot correspondant
                                </p>
                            </div>
                        )}



                        {motionDescription && (
                            <div className="bg-gray-800 p-4 rounded-xl border border-green-500/30 flex-shrink-0 shadow-md">
                                <div className="text-xs text-green-500 font-bold uppercase mb-1 tracking-wider">
                                    Motion Vector
                                </div>
                                <p className="text-sm text-green-100 leading-relaxed">
                                    {motionDescription}
                                </p>
                            </div>
                        )}
                        {/* Always show reference/active image if it exists */}
                        {displayImage && (
                            <div className="relative group w-full aspect-video bg-black rounded-xl overflow-hidden flex-shrink-0 border border-gray-700 shadow-lg">
                                <img
                                    src={URL.createObjectURL(displayImage.file)}
                                    alt="Visual Context"
                                    className="w-full h-full object-contain"
                                />
                                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-2">
                                    <span className="text-xs text-white font-medium">
                                        {activeChatImage ? "Working Image" : "Visual Anchor"}
                                    </span>
                                </div>
                            </div>
                        )}
                        {/* Show mentioned characters in a separate section */}
                        {mentionedCharacters.length > 0 && (
                            <div className="flex flex-col gap-3">
                                <div className="text-xs font-bold text-gray-500 uppercase tracking-wider pl-1">
                                    Cast on Set
                                </div>
                                {mentionedCharacters.map((char) => (
                                    <div
                                        key={char.id}
                                        className="flex items-center gap-3 bg-gray-800/80 p-2 rounded-xl border border-gray-700 hover:border-indigo-500 transition-colors">
                                        <div className="w-12 h-12 bg-black rounded-lg overflow-hidden flex-shrink-0">
                                            {char.images.length > 0 && (
                                                <img
                                                    src={`data:${char.images[0].type};base64,${char.images[0].base64}`}
                                                    alt={char.name}
                                                    className="w-full h-full object-cover"
                                                />
                                            )}
                                        </div>
                                        <div className="flex flex-col overflow-hidden">
                                            <span className="font-semibold text-gray-200 truncate text-sm">
                                                {char.name}
                                            </span>
                                            <span className="text-xs text-gray-500 truncate">
                                                {char.voiceName || 'No voice assigned'}
                                            </span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};
